/**
 * English interface catalogue — **the source of the keys** (D260814d).
 *
 * `messages-fr.ts` is typed against this object: a key missing there, or one
 * whose parameters differ, fails `pnpm typecheck` rather than showing an English
 * word in the middle of a French sentence. Add a message here first.
 *
 * A message is a whole sentence, or a function of what varies inside it. Never
 * assemble one from fragments at the call site: "3" + " items" cannot be
 * translated into a language that agrees the noun differently, and the fragments
 * arrive in the catalogue without the sentence that would explain them.
 *
 * Deliberately **not** `as const`: the type of this object is the contract the
 * French catalogue has to satisfy, and literal types would oblige it to repeat
 * the English sentences word for word.
 */
export const en = {
  /* ------------------------------------------------------------- Everywhere */

  'common.loadingLabel': 'Loading',

  'password.show': 'Show password',
  'password.hide': 'Hide password',

  /* ---------------------------------------------------------------- Sign in */

  'login.subtitle': 'Sign in to pick the day back up.',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  // Only ever shown when the network failed before the server could answer:
  // every refusal arrives with its own message, already translated.
  'login.failed': 'The server could not be reached. Try again.',
  'login.noAccount': 'This instance has no account yet.',
  'login.createAdmin': 'Create the first one from a shell on the server, then sign in here.',

  /* ---------------------------------------------------------------- Top bar */

  'topbar.signOut': 'Sign out',
  'topbar.language': 'Language',

  /* ------------------------------------------------------------- Navigation */

  'nav.modules': 'Modules',
  // The two modules that are named on the rail without being built. The word is
  // there so that a tab which does nothing reads as work not yet done rather than
  // as something broken.
  'nav.soon': 'Soon',

  'module.todos': 'Todos',
  'module.reminders': 'Reminders',
  'module.notes': 'Notes',
  'module.snippets': 'Snippets',

  /* ----------------------------------------------------------------- Search */

  'search.open': 'Search',
  'search.shortcut': 'Search (Ctrl+K)',
  'search.placeholder': 'Search everything, or #tag',
  // Not "no results": an empty field has not asked anything yet, and what it
  // shows is what was touched last.
  'search.empty': 'What you were last working on.',
  'search.nothing': 'Nothing matches that.',
  'search.hint': '↑ ↓ to move, Enter to open, Esc to close.',
  // There is no index in the browser: the service worker caches nothing under
  // `/api/`, which is what keeps one account's data off a shared device.
  'search.offline': 'Nothing is searchable without a connection.',
  'search.failed': 'The search could not be run.',

  /* ------------------------------------------------------------------- Tags */

  // One vocabulary for every module (D260815), so these sentences are not
  // `todos.*` or `reminders.*`: the same field is written on both screens, and
  // two copies would eventually name the same thing differently.
  'tags.label': 'Tags',
  'tags.add': 'Add a tag',
  'tags.remove': (name: string) => `Remove the tag “${name}”`,
  // Shown only once the rule has been broken, and before the server sees it: a
  // 400 arrives after the field has been cleared, which is too late to correct.
  'tags.rule': 'Lower-case letters, digits and hyphens, with no spaces.',
  'tags.full': (count: number) => `${count} tags is as many as one row can carry.`,
  'tags.suggestions': 'Existing tags',
  'tags.filters': 'Filter by tag',
  'tags.all': 'All',

  /* ------------------------------------------------------------------ Todos */

  'todos.add': 'Add a todo',
  'todos.addAction': 'Add',
  'todos.due': 'Due date',
  'todos.clearDue': 'No due date',

  'todos.filters': 'Show',
  'todos.filterOpen': 'Open',
  'todos.filterToday': 'Today',
  'todos.filterAll': 'All',
  'todos.done': 'Done',

  // The checkbox has no visible label — the title beside it is the label a person
  // reads. These are what a screen reader announces instead, and they name the
  // todo because a list of them all saying "Done" would be unusable.
  'todos.markDone': (title: string) => `Mark “${title}” as done`,
  'todos.markOpen': (title: string) => `Mark “${title}” as still to do`,
  'todos.rename': (title: string) => `Rename “${title}”`,
  'todos.archive': (title: string) => `Archive “${title}”`,
  'todos.actions': 'Actions',

  'todos.dueToday': 'Today',
  'todos.dueTomorrow': 'Tomorrow',
  'todos.dueYesterday': 'Yesterday',

  'todos.emptyTitle': 'Nothing on the list',
  'todos.emptyBody': 'Whatever the day is made of goes in here.',
  'todos.nothingHere': 'Nothing under this filter',
  'todos.nothingHereBody': 'There are todos on the list, just none of them here.',
  'todos.allDoneTitle': 'Everything is done',
  'todos.allDone': (count: number) =>
    count === 1 ? 'One todo finished.' : `${count} todos finished.`,

  'todos.errorTitle': 'The list could not be loaded',
  // Only ever shown when the request never reached the server: every refusal
  // arrives with its own message, already translated.
  'todos.errorFallback': 'The server could not be reached.',
  'todos.retry': 'Try again',

  'todos.offlineTitle': 'No connection',
  'todos.offlineBody':
    'The list is never stored on this device, so there is nothing to show until the connection is back.',

  'todos.archived': (count: number) =>
    count === 1 ? '1 todo archived' : `${count} todos archived`,
  'todos.undo': 'Undo',
  'todos.dismiss': 'Dismiss',

  /* -------------------------------------------------------------- Reminders */

  'reminders.add': 'Add a reminder',
  'reminders.addAction': 'Add',
  'reminders.at': 'Time',

  'reminders.filters': 'Show',
  'reminders.filterNext': 'Next',
  'reminders.filterToday': 'Today',
  'reminders.filterAll': 'All',
  'reminders.done': 'Done',

  // The moment, right-aligned in a row. A whole phrase rather than a day and a
  // time glued together at the call site: a language that wanted "à 14:30" has
  // nowhere to put the preposition otherwise.
  'reminders.atToday': (time: string) => `Today ${time}`,
  'reminders.atTomorrow': (time: string) => `Tomorrow ${time}`,
  'reminders.atYesterday': (time: string) => `Yesterday ${time}`,
  'reminders.atDay': (day: string, time: string) => `${day} ${time}`,

  'reminders.markDone': (title: string) => `Mark “${title}” as dealt with`,
  'reminders.markStanding': (title: string) => `Mark “${title}” as still standing`,
  'reminders.rename': (title: string) => `Rename “${title}”`,
  'reminders.archive': (title: string) => `Archive “${title}”`,

  'reminders.emptyTitle': 'Nothing coming up',
  'reminders.emptyBody': 'Whatever has to happen at a particular time goes in here.',
  'reminders.nothingHere': 'Nothing under this filter',
  'reminders.nothingHereBody': 'There are reminders on the list, just none of them here.',
  'reminders.allDoneTitle': 'Everything is dealt with',
  'reminders.allDone': (count: number) =>
    count === 1 ? 'One reminder dealt with.' : `${count} reminders dealt with.`,

  'reminders.errorTitle': 'The reminders could not be loaded',
  // Only ever shown when the request never reached the server: every refusal
  // arrives with its own message, already translated.
  'reminders.errorFallback': 'The server could not be reached.',
  'reminders.retry': 'Try again',

  'reminders.archived': (count: number) =>
    count === 1 ? '1 reminder archived' : `${count} reminders archived`,
  'reminders.undo': 'Undo',
  'reminders.dismiss': 'Dismiss',

  /* ------------------------------------------------------------------ Notes */

  'notes.new': 'New note',
  'notes.untitled': 'Untitled',
  'notes.body': 'Note',
  'notes.placeholder': 'Markdown. The first line becomes the title.',
  'notes.view': 'View',
  'notes.write': 'Write',
  'notes.preview': 'Preview',
  'notes.saving': 'Saving…',
  'notes.saved': 'Saved',
  'notes.saveFailed': 'Not saved',
  'notes.backToList': 'Back to the notes',
  'notes.missingTitle': 'This note is not here',

  'notes.folders': 'Folders',
  'notes.folder': 'Folder',
  'notes.folderName': 'Folder name',
  'notes.everything': 'Everything',
  // A note that has not been given a place yet, rather than a second kind of
  // note: quick capture writes here, and filing is what happens afterwards.
  'notes.inbox': 'Inbox',
  'notes.newFolder': 'New folder',
  'notes.renameFolder': (name: string) => `Rename “${name}”`,
  'notes.deleteFolder': (name: string) => `Delete “${name}”`,
  // Only ever shown when the request never reached the server: a refusal — a
  // folder that still holds something, a name a sibling already has — arrives
  // with its own sentence, already translated.
  'notes.folderFailed': 'The server could not be reached.',

  'notes.emptyTitle': 'Nothing written yet',
  'notes.emptyBody': 'What has to be remembered but is not a task goes in here.',
  'notes.nothingHere': 'Nothing here',
  'notes.nothingHereBody': 'There are notes, just none in this folder.',

  'notes.errorTitle': 'The notes could not be loaded',
  'notes.errorFallback': 'The server could not be reached.',
  'notes.retry': 'Try again',

  'notes.archive': (title: string) => `Archive “${title}”`,
  'notes.archived': (count: number) =>
    count === 1 ? '1 note archived' : `${count} notes archived`,
  'notes.undo': 'Undo',
  'notes.dismiss': 'Dismiss',

  /* --------------------------------------------------------- Quick capture */

  'notes.capture': 'Write a note',
  'notes.captureShortcut': 'Write a note (n)',
  'notes.capturePlaceholder': 'Anything worth keeping.',
  'notes.captureAction': 'Keep',
  'notes.captureHint': 'Enter to keep it, Shift+Enter for a new line.',
  'notes.captureFailed': 'That note could not be kept.',
  // The one thing in kanto that survives on the device, so it says so rather
  // than looking like a note that has been stored.
  'notes.waiting': (count: number) =>
    count === 1 ? '1 note waiting for the connection' : `${count} notes waiting for the connection`,

  /* ------------------------------------------------- Notifications, per device */

  'push.title': 'Notifications on this device',
  'push.on': 'On',
  'push.off': 'Off',
  'push.enable': 'Turn on',
  'push.disable': 'Turn off',
  'push.working': 'Just a moment…',
  // "even with kanto closed" was the first wording, and it was true in a way
  // that reads as more than it says: the service worker lives in the browser, so
  // quitting the browser on a laptop stops notifications until it starts again.
  // On a phone the system wakes it, which is the case worth promising.
  'push.onBody': 'Reminders will ring while the browser is running — on a phone, even closed.',
  'push.offBody': 'Reminders will show on this screen, but nothing will ring.',
  // Every device is asked separately, because the permission belongs to the
  // browser and not to the account.
  'push.perDevice': 'Each device is asked separately.',
  'push.deniedTitle': 'Notifications are blocked',
  'push.deniedBody':
    'This browser has been told to refuse them. The site settings beside the address bar are the only way back.',
  'push.unsupportedTitle': 'This browser cannot ring',
  // The iOS caveat, stated as the fact it is: Safari only allows Web Push once
  // the application has been added to the Home Screen. Reporting the browser as
  // broken would send somebody looking for a fault that is not there.
  'push.unsupportedBody':
    'On iPhone and iPad, add kanto to the Home Screen first — Safari only allows notifications there.',
  'push.unavailableTitle': 'Notifications are not available',
  'push.unavailableBody': 'The server did not hand out a key, so nothing can be subscribed.',
  // A browser is not obliged to put the permission request on screen. Chrome
  // replaces it with an icon in the address bar once somebody has waved one
  // away, and answers "undecided" without asking — so the button appears to do
  // nothing, and this sentence is the only thing that says where to go instead.
  'push.dismissed':
    'This browser did not ask. Allow notifications for this site from the icon in the address bar, then press Turn on again.',
  'push.failed': 'That did not work. Try again.',
};

/** The shape every catalogue has to have. */
export type Messages = typeof en;
